/**
 * 실루엣 추출 — "무엇이 캐릭터이고 무엇이 배경인가".
 *
 * 이 판단 하나가 뒤의 모든 걸 좌우한다. 마스크가 틀리면 메시가 배경까지 부풀리고,
 * 팔레트가 배경색에 오염되고, 러프니스가 벽지 무늬를 읽는다.
 *
 * 순서:
 *   1. 알파가 있으면 알파를 믿는다 (PNG 스프라이트 — 게임 에셋의 태반).
 *   2. 없으면 네 변에서 플러드필. 테두리 색이 균일할 때만 유효하다.
 *   3. 테두리가 시끄러우면 포기하고 화면 전체를 피사체로 둔다 (릴리프 모드용).
 */

import type { Field, Raster } from '../types.ts';
import { clamp, gaussianBlur, makeField } from './raster.ts';

export interface SilhouetteResult {
  mask: Field;
  source: 'alpha' | 'flood' | 'full';
  coverage: number;
}

/** 알파 채널이 실제로 뭔가를 잘라내고 있는가. */
function hasMeaningfulAlpha(src: Raster): boolean {
  const { data } = src;
  let transparent = 0;
  const total = data.length / 4;
  const stride = Math.max(4, Math.floor(total / 20000) * 4);
  let checked = 0;
  for (let p = 3; p < data.length; p += stride) {
    checked++;
    if (data[p]! < 250) transparent++;
  }
  // 표본의 2% 이상이 반투명 이하 → 의도된 알파로 본다.
  return checked > 0 && transparent / checked > 0.02;
}

/**
 * 테두리 픽셀들이 얼마나 한 가지 색인지. 크면 배경이 복잡하다는 뜻.
 *
 * **절사 평균**을 쓴다. 피사체가 한 변에 닿으면(머리 클로즈업의 목·옷깃이 아래 변에 닿는다)
 * 테두리 표본의 4분의 1이 피사체 색이 되어 편차가 46 을 넘고, 그러면 배경 제거를 포기해
 * 화면 전체가 피사체가 된다(실측: 합성 클로즈업이 통째로 실루엣). 중앙값에서 가까운 70% 만으로
 * 평균·편차를 재면 한두 변이 가려져도 배경색을 제대로 집는다 — 플러드필은 어차피 씨앗 색에
 * 가까운 테두리 픽셀에서만 퍼지므로 나머지 변으로 배경을 찾는다.
 */
function borderSpread(src: Raster): { mean: [number, number, number]; spread: number } {
  const { width: w, height: h, data } = src;
  const samples: number[][] = [];
  const push = (x: number, y: number) => {
    const p = (y * w + x) * 4;
    samples.push([data[p]!, data[p + 1]!, data[p + 2]!]);
  };
  const step = Math.max(1, Math.floor(Math.max(w, h) / 128));
  for (let x = 0; x < w; x += step) { push(x, 0); push(x, h - 1); }
  for (let y = 0; y < h; y += step) { push(0, y); push(w - 1, y); }

  if (samples.length === 0) return { mean: [255, 255, 255], spread: 0 };
  const med: [number, number, number] = [0, 1, 2].map((c) => {
    const v = samples.map((s) => s[c]!).sort((a, b) => a - b);
    return v[v.length >> 1]!;
  }) as [number, number, number];
  const d2 = (s: number[]) => (s[0]! - med[0]) ** 2 + (s[1]! - med[1]) ** 2 + (s[2]! - med[2]) ** 2;
  const kept = samples.slice().sort((a, b) => d2(a) - d2(b)).slice(0, Math.max(4, Math.round(samples.length * 0.7)));

  const mean: [number, number, number] = [0, 0, 0];
  for (const s of kept) { mean[0] += s[0]!; mean[1] += s[1]!; mean[2] += s[2]!; }
  mean[0] /= kept.length; mean[1] /= kept.length; mean[2] /= kept.length;

  let variance = 0;
  for (const s of kept) {
    variance += (s[0]! - mean[0]) ** 2 + (s[1]! - mean[1]) ** 2 + (s[2]! - mean[2]) ** 2;
  }
  return { mean, spread: Math.sqrt(variance / kept.length) };
}

/**
 * 네 변에서 시작하는 4-이웃 플러드필.
 * 씨앗 색과의 거리로 판정하되, 전파하면서 기준색을 갱신하지 않는다 —
 * 갱신하면 그라데이션 배경에서 피사체까지 먹어버린다.
 */
function floodBackground(src: Raster, seed: [number, number, number], tolerance: number): Uint8Array {
  const { width: w, height: h, data } = src;
  const bg = new Uint8Array(w * h);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;
  const tol2 = tolerance * tolerance;

  const near = (idx: number) => {
    const p = idx * 4;
    const dr = data[p]! - seed[0];
    const dg = data[p + 1]! - seed[1];
    const db = data[p + 2]! - seed[2];
    return dr * dr + dg * dg + db * db <= tol2;
  };

  const push = (idx: number) => {
    if (bg[idx] === 1) return;
    if (!near(idx)) return;
    bg[idx] = 1;
    queue[tail++] = idx;
  };

  for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }

  while (head < tail) {
    const idx = queue[head++]!;
    const x = idx % w;
    const y = (idx / w) | 0;
    if (x > 0) push(idx - 1);
    if (x < w - 1) push(idx + 1);
    if (y > 0) push(idx - w);
    if (y < h - 1) push(idx + w);
  }
  return bg;
}

/** 이진 마스크에 대한 침식/팽창. 체비쇼프 반경. */
function morph(mask: Uint8Array, w: number, h: number, radius: number, dilate: boolean): Uint8Array {
  if (radius < 1) return mask;
  let cur = mask;
  for (let pass = 0; pass < radius; pass++) {
    const next = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        let acc = dilate ? 0 : 1;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= h) { if (!dilate) acc = 0; continue; }
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= w) { if (!dilate) acc = 0; continue; }
            const v = cur[yy * w + xx]!;
            if (dilate) { if (v) acc = 1; }
            else if (!v) acc = 0;
          }
        }
        next[i] = acc;
      }
    }
    cur = next;
  }
  return cur;
}

/**
 * 갇힌 배경 구멍 뚫기.
 *
 * 테두리 플러드필은 테두리와 이어진 배경만 걷어낸다. 팔과 몸통 사이, 다리 사이처럼
 * 실루엣에 **갇힌** 배경은 그대로 피사체로 남아 메시에 회색 판이 생긴다 — 갤러리의
 * 트렌트에서 실제로 보였다. 그래서 테두리와 안 이어졌어도 배경색과 **아주** 가깝고
 * 어느 정도 크기가 되는 덩어리는 배경으로 돌린다.
 *
 * 허용치를 절반으로 죄는 이유: 흰 배경 위의 흰 털복숭이 같은 그림에서 피사체의
 * 밝은 부분까지 뚫리면 안 된다. 진짜 배경은 정확히 그 색이고, 털은 그렇지 않다.
 */
function punchEnclosedHoles(
  src: Raster,
  bg: Uint8Array,
  seed: [number, number, number],
  tolerance: number,
  minSize: number,
): void {
  const { width: w, height: h, data } = src;
  const n = w * h;
  const tol2 = (tolerance * 0.5) ** 2;
  const candidate = new Uint8Array(n);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    if (bg[i]) continue;
    const dr = data[p]! - seed[0];
    const dg = data[p + 1]! - seed[1];
    const db = data[p + 2]! - seed[2];
    candidate[i] = dr * dr + dg * dg + db * db <= tol2 ? 1 : 0;
  }

  const visited = new Uint8Array(n);
  const queue = new Int32Array(n);
  for (let start = 0; start < n; start++) {
    if (!candidate[start] || visited[start]) continue;
    let head = 0, tail = 0;
    queue[tail++] = start;
    visited[start] = 1;
    while (head < tail) {
      const idx = queue[head++]!;
      const x = idx % w;
      const y = (idx / w) | 0;
      const visit = (m: number) => {
        if (candidate[m] && !visited[m]) { visited[m] = 1; queue[tail++] = m; }
      };
      if (x > 0) visit(idx - 1);
      if (x < w - 1) visit(idx + 1);
      if (y > 0) visit(idx - w);
      if (y < h - 1) visit(idx + w);
    }
    if (tail >= minSize) {
      for (let k = 0; k < tail; k++) bg[queue[k]!] = 1;
    }
  }
}

/** 가장 큰 연결 성분만 남긴다. 배경에 떠 있는 티끌과 UI 조각을 걷어낸다. */
function keepLargestComponent(mask: Uint8Array, w: number, h: number): Uint8Array {
  const label = new Int32Array(w * h).fill(-1);
  const queue = new Int32Array(w * h);
  let best = -1;
  let bestSize = 0;
  let current = 0;

  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || label[start]! >= 0) continue;
    let head = 0, tail = 0;
    queue[tail++] = start;
    label[start] = current;
    let size = 0;
    while (head < tail) {
      const idx = queue[head++]!;
      size++;
      const x = idx % w;
      const y = (idx / w) | 0;
      const visit = (n: number) => {
        if (mask[n] && label[n]! < 0) { label[n] = current; queue[tail++] = n; }
      };
      if (x > 0) visit(idx - 1);
      if (x < w - 1) visit(idx + 1);
      if (y > 0) visit(idx - w);
      if (y < h - 1) visit(idx + w);
    }
    if (size > bestSize) { bestSize = size; best = current; }
    current++;
  }

  if (best < 0) return mask;
  const out = new Uint8Array(w * h);
  for (let i = 0; i < out.length; i++) out[i] = label[i] === best ? 1 : 0;
  return out;
}

export interface SilhouetteOptions {
  /** 가장 큰 연결 성분만 남길지. 캐릭터 시트처럼 피사체가 여럿이면 false. */
  keepLargest?: boolean;
}

export function extractSilhouette(src: Raster, opts: SilhouetteOptions = {}): SilhouetteResult {
  const keepLargest = opts.keepLargest ?? true;
  const { width: w, height: h, data } = src;
  const n = w * h;
  let binary: Uint8Array | null = null;
  let source: SilhouetteResult['source'] = 'full';

  if (hasMeaningfulAlpha(src)) {
    binary = new Uint8Array(n);
    for (let i = 0, p = 3; i < n; i++, p += 4) binary[i] = data[p]! >= 128 ? 1 : 0;
    source = 'alpha';
  } else {
    const { mean, spread } = borderSpread(src);
    // 테두리 색 편차가 이 정도를 넘으면 배경이 사진처럼 복잡하다는 뜻.
    if (spread < 46) {
      const tolerance = Math.max(28, Math.min(90, spread * 2.6 + 26));
      const bg = floodBackground(src, mean, tolerance);
      punchEnclosedHoles(src, bg, mean, tolerance, Math.max(40, Math.round(n * 0.0004)));
      const fg = new Uint8Array(n);
      let count = 0;
      for (let i = 0; i < n; i++) { fg[i] = bg[i] ? 0 : 1; if (fg[i]) count++; }
      // 전경이 너무 적거나(잘못 먹음) 너무 많으면(안 먹음) 신뢰하지 않는다.
      const ratio = count / n;
      if (ratio > 0.04 && ratio < 0.96) {
        binary = fg;
        source = 'flood';
      }
    }
  }

  if (!binary) {
    const mask = makeField(w, h, 1);
    return { mask, source: 'full', coverage: 1 };
  }

  // 구멍 메우기 → 티끌 제거 → 최대 성분.
  const r = Math.max(1, Math.round(Math.max(w, h) / 400));
  binary = morph(binary, w, h, r, true);
  binary = morph(binary, w, h, r, false);
  if (keepLargest) binary = keepLargestComponent(binary, w, h);

  const mask = makeField(w, h);
  let count = 0;
  for (let i = 0; i < n; i++) { mask.data[i] = binary[i]!; count += binary[i]!; }

  // 계단 현상을 없애는 최소한의 페더링. 과하면 실루엣이 물러진다.
  const feathered = gaussianBlur(mask, Math.max(0.8, Math.max(w, h) / 700));
  for (let i = 0; i < n; i++) feathered.data[i] = clamp(feathered.data[i]!);

  return { mask: feathered, source, coverage: count / n };
}

// ---------------------------------------------------------------------------
// 거리 변환
// ---------------------------------------------------------------------------

const EDT_INF = 1e20;

/** Felzenszwalb & Huttenlocher 1D 제곱거리 변환. 포물선 하부포락선. */
function edt1d(f: Float64Array, d: Float64Array, v: Int32Array, z: Float64Array, n: number): void {
  let k = 0;
  v[0] = 0;
  z[0] = -EDT_INF;
  z[1] = EDT_INF;
  for (let q = 1; q < n; q++) {
    let s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
    while (s <= z[k]!) {
      k--;
      s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = EDT_INF;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1]! < q) k++;
    const dx = q - v[k]!;
    d[q] = dx * dx + f[v[k]!]!;
  }
}

/**
 * 피사체 내부 각 점에서 실루엣까지의 유클리드 거리.
 * 반환값은 최대값으로 나눈 0..1 — 부풀리기 가중치로 바로 쓴다.
 */
export function distanceTransform(mask: Field, threshold = 0.5, normalize = true): Field {
  const { width: w, height: h } = mask;
  const grid = new Float64Array(w * h);
  for (let i = 0; i < grid.length; i++) grid[i] = mask.data[i]! >= threshold ? EDT_INF : 0;

  const maxDim = Math.max(w, h);
  const f = new Float64Array(maxDim);
  const d = new Float64Array(maxDim);
  const v = new Int32Array(maxDim);
  const z = new Float64Array(maxDim + 1);

  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = grid[y * w + x]!;
    edt1d(f, d, v, z, h);
    for (let y = 0; y < h; y++) grid[y * w + x] = d[y]!;
  }
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) f[x] = grid[row + x]!;
    edt1d(f, d, v, z, w);
    for (let x = 0; x < w; x++) grid[row + x] = d[x]!;
  }

  const out = makeField(w, h);
  let max = 0;
  for (let i = 0; i < out.data.length; i++) {
    const dist = Math.sqrt(grid[i]!);
    out.data[i] = dist;
    if (dist > max) max = dist;
  }
  if (!normalize) return out;
  const inv = max > 0 ? 1 / max : 0;
  for (let i = 0; i < out.data.length; i++) out.data[i] *= inv;
  return out;
}
